import { useTranslations } from 'next-intl';
import type { LucideIcon } from 'lucide-react';
import {
  CodeIcon,
  ZapIcon,
  MonitorIcon,
  PaletteIcon,
  CloudIcon,
  CpuIcon,
  BarChartIcon,
  BotIcon,
  ShieldIcon,
  LockIcon,
  HeadphonesIcon,
  LightbulbIcon,
  HeartIcon,
  DatabaseIcon,
  UserIcon,
  FileTextIcon,
  SettingsIcon,
  FileIcon,
  GlobeIcon,
  FolderIcon,
  StarIcon,
  HelpCircleIcon,
} from 'lucide-react';
import { isFeatureEnabled } from '@/config/navigationFeatures';

export interface NavigationChild {
  id: string;
  label: string;
  href: string;
  icon?: LucideIcon;
}

export interface NavigationItem {
  id: string;
  label: string;
  href: string;
  hasDropdown: boolean;
  children?: NavigationChild[];
}

type NavLinkConfig = {
  id: string;
  href: string;
  labelKey: string;
  fallback: string;
  icon?: LucideIcon;
};

const buildChildren = (
  links: NavLinkConfig[],
  t: ReturnType<typeof useTranslations>
): NavigationChild[] =>
  links.map(({ id, href, icon, fallback, labelKey }) => ({
    id,
    href,
    icon,
    label: t(labelKey, { defaultMessage: fallback }),
  }));

export const useNavigation = (): NavigationItem[] => {
  const t = useTranslations('megaMenu');
  const items: NavigationItem[] = [];


  if (isFeatureEnabled('solutions')) {
    const solutionLinks: NavLinkConfig[] = [
      { id: 'web-development', href: '/solutions/web-development', icon: CodeIcon, labelKey: 'solutions.section1.item1.label', fallback: 'Web Development' },
      { id: 'mobile-apps', href: '/solutions/mobile-apps', icon: ZapIcon, labelKey: 'solutions.section1.item2.label', fallback: 'Mobile Applications' },
      { id: 'desktop-apps', href: '/solutions/desktop-apps', icon: MonitorIcon, labelKey: 'solutions.section1.item3.label', fallback: 'Desktop Applications' },
      { id: 'ui-ux-design', href: '/solutions/design', icon: PaletteIcon, labelKey: 'solutions.section1.item4.label', fallback: 'UI/UX Design' },
      { id: 'cloud-solutions', href: '/solutions/cloud', icon: CloudIcon, labelKey: 'solutions.section2.item1.label', fallback: 'Cloud Solutions' },
      { id: 'ai-ml', href: '/solutions/ai-ml', icon: CpuIcon, labelKey: 'solutions.section2.item2.label', fallback: 'AI & Machine Learning' },
      { id: 'data-analytics', href: '/solutions/analytics', icon: BarChartIcon, labelKey: 'solutions.section2.item3.label', fallback: 'Data Analytics' },
      { id: 'process-automation', href: '/solutions/automation', icon: BotIcon, labelKey: 'solutions.section2.item4.label', fallback: 'Process Automation' },
      { id: 'cybersecurity', href: '/solutions/cybersecurity', icon: ShieldIcon, labelKey: 'solutions.section3.item1.label', fallback: 'Cybersecurity' },
      { id: 'data-protection', href: '/solutions/data-protection', icon: LockIcon, labelKey: 'solutions.section3.item2.label', fallback: 'Data Protection' },
      { id: 'maintenance-support', href: '/solutions/support', icon: HeadphonesIcon, labelKey: 'solutions.section3.item3.label', fallback: 'Maintenance & Support' },
      { id: 'it-consulting', href: '/solutions/consulting', icon: LightbulbIcon, labelKey: 'solutions.section3.item4.label', fallback: 'IT Consulting' },
    ];

    items.push({
      id: 'solutions',
      label: t('solutions.title'),
      href: '/solutions',
      hasDropdown: true,
      children: buildChildren(solutionLinks, t),
    });
  }


  // if (isFeatureEnabled('industries')) {
  //   const industryLinks: NavLinkConfig[] = [
  //     { id: 'finance', href: '/industries/finance', icon: DatabaseIcon, labelKey: 'industries.section1.item1.label', fallback: 'Financial' },
  //     { id: 'healthcare', href: '/industries/healthcare', icon: UserIcon, labelKey: 'industries.section1.item2.label', fallback: 'Healthcare' },
  //     { id: 'ecommerce', href: '/industries/ecommerce', icon: GlobeIcon, labelKey: 'industries.section1.item3.label', fallback: 'Commerce' },
  //     { id: 'education', href: '/industries/education', icon: FileTextIcon, labelKey: 'industries.section1.item4.label', fallback: 'Education' },
  //     { id: 'manufacturing', href: '/industries/manufacturing', icon: SettingsIcon, labelKey: 'industries.section1.item5.label', fallback: 'Manufacturing' },
  //   ];

  //   items.push({
  //     id: 'industries',
  //     label: t('industries.title'),
  //     href: '/industries',
  //     hasDropdown: true,
  //     children: buildChildren(industryLinks, t),
  //   });
  // }

  const companyLinks: NavLinkConfig[] = [
    { id: 'about', href: '/about', icon: UserIcon, labelKey: 'company.section1.item1.label', fallback: 'Our Story' },
    { id: 'values-culture', href: '/about#values', icon: HeartIcon, labelKey: 'company.section1.item2.label', fallback: 'Values & Culture' },
    { id: 'team', href: '/about#team', icon: UserIcon, labelKey: 'company.section1.item3.label', fallback: 'Our Team' },
    { id: 'ai-innovation', href: '/about#ai-innovation', icon: CpuIcon, labelKey: 'company.section1.item4.label', fallback: 'AI Innovation' },
    { id: 'industry-experience', href: '/about#industry-experience', icon: GlobeIcon, labelKey: 'company.section1.item5.label', fallback: 'Industry Experience' },
    { id: 'client-reviews', href: '/about#reviews', icon: StarIcon, labelKey: 'company.section1.item6.label', fallback: 'Client Reviews' },
    { id: 'ask-questions', href: '/about#ask-questions', icon: HelpCircleIcon, labelKey: 'company.section1.item7.label', fallback: 'Ask Questions' },
    { id: 'digital-solutions', href: '/about#solutions', icon: CodeIcon, labelKey: 'company.section1.item8.label', fallback: 'Digital Solutions' },
  ];

  items.push({
    id: 'company',
    label: t('company.title'),
    href: '/company',
    hasDropdown: true,
    children: buildChildren(companyLinks, t),
  });

  items.push({
    id: 'services-direct',
    label: t('services.title'),
    href: '/services',
    hasDropdown: false,
  });


  items.push({
    id: 'projects',
    label: t('projects.title'),
    href: '/projects',
    hasDropdown: false,
  });


  items.push({
    id: 'feedback',
    label: t('feedback.title'),
    href: '/feedback',
    hasDropdown: false,
  });


  return items;
};